import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, FilePlus2, MessageCircle, Pencil, Phone, Trash2, HardHat } from "lucide-react";
import { api } from "@/lib/api";
import { Btn, Empty, Spinner } from "@/components/rp";
import { CantiereCard } from "@/components/CantiereCard";
import { AppointmentDialog } from "@/components/admin/AppointmentDialog";
import { QuoteFormDialog } from "@/components/admin/quoteDialogs";
import { ProvenienzaCard } from "@/components/admin/ProvenienzaCard";
import { ClienteEditDialog, ClienteLogistica } from "@/components/admin/ClienteEdit";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { SopralluoghiPanel } from "@/components/admin/sopralluoghi";
import { toast } from "sonner";

export default function AdminClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { api.get(`/admin/clients/${id}`).then((r) => setC(r.data)).catch(() => navigate("/admin/clienti")); }, [id, navigate]);
  const remove = async (confirm) => {
    await api.delete(`/admin/clients/${c.id}`, { params: { confirm } });
    toast.success(`Cliente ${c.name} eliminato con tutti i suoi dati`);
    navigate("/admin/clienti");
  };

  if (!c) return <Spinner />;
  return (
    <div className="space-y-8">
      <Link to="/admin/clienti" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="back-to-clients"><ArrowLeft className="h-4 w-4" /> Clienti</Link>
      <div className="rp-card p-6 sm:p-8 flex flex-wrap items-center gap-6 rp-fade">
        <span className="h-16 w-16 rounded-full bg-[#E6DDCE] flex items-center justify-center text-2xl font-semibold">{c.name.charAt(0).toUpperCase()}</span>
        <div className="flex-1 min-w-[200px]">
          <p className="rp-label">Cliente</p>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="client-detail-name">{c.name}</h1>
          <p className="text-sm text-[#757575] mt-1" data-testid="client-detail-contacts">{[c.email, c.phone, c.address].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.phone && <a href={`tel:${c.phone}`}><Btn variant="secondary" data-testid="client-call-button"><Phone className="h-4 w-4" /> Chiama</Btn></a>}
          <Btn variant="secondary" onClick={() => navigate(`/admin/messaggi/${c.id}`)} data-testid="client-chat-button"><MessageCircle className="h-4 w-4" /> Chat</Btn>
          <Btn variant="secondary" onClick={() => setDialog("appt")} data-testid="client-appointment-button"><CalendarPlus className="h-4 w-4" /> Appuntamento</Btn>
          <Btn variant="outline" onClick={() => setDialog("edit")} data-testid="client-edit-button"><Pencil className="h-4 w-4" /> Modifica anagrafica</Btn>
          <Btn onClick={() => setDialog("quote")} data-testid="client-new-quote-button"><FilePlus2 className="h-4 w-4" /> Nuovo preventivo</Btn>
        </div>
      </div>
      <ProvenienzaCard c={c} onSaved={(d) => setC({ ...c, ...d })} />
      <section className="rp-card p-5 sm:p-6" data-testid="client-logistica">
        <p className="rp-label mb-3">Abitazione e logistica</p>
        <ClienteLogistica c={c} />
      </section>
      <section className="rp-card p-5 sm:p-6" data-testid="client-sopralluoghi">
        <SopralluoghiPanel compact filter={{ client_id: c.id }} preset={{ client_id: c.id, client_name: c.name, address: c.address || "", piano: c.logistica?.delivery_floor || "", ascensore: c.logistica?.elevator ?? null, ztl: c.logistica?.ztl ?? null }} />
      </section>
      <section>
        <p className="rp-label mb-3">Cantieri</p>
        {!c.cantieri.length ? <Empty icon={HardHat} title="Nessun cantiere" text="Crea un preventivo: diventa cantiere quando il cliente lo accetta o con «Trasforma in cantiere»." /> : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="client-cantieri-list">{c.cantieri.map((x) => <CantiereCard key={x.id} c={{ ...x, client_name: c.name }} />)}</div>
        )}
      </section>
      <div className="pt-4 border-t border-[#EBEBEB]">
        <Btn variant="danger" onClick={() => setDialog("delete")} data-testid="client-delete-button"><Trash2 className="h-4 w-4" /> Elimina cliente</Btn>
      </div>
      <AppointmentDialog open={dialog === "appt"} onClose={() => setDialog(null)} clientId={c.id} />
      <QuoteFormDialog open={dialog === "quote"} onClose={() => setDialog(null)} clientId={c.id} onSaved={(q) => navigate(`/admin/preventivi/${q.id}`)} />
      <ClienteEditDialog c={c} open={dialog === "edit"} onClose={() => setDialog(null)} onSaved={(u) => setC({ ...c, ...u })} />
      <ConfirmDelete open={dialog === "delete"} onClose={() => setDialog(null)} title={`Eliminare ${c.name}?`} word={c.name} testid="client-delete-dialog"
        what="il cliente, i suoi preventivi, cantieri, documenti, foto, diario, messaggi e appuntamenti" onConfirm={remove} />
    </div>
  );
}
